import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, LogOut, ArrowLeftRight, AlertTriangle, Building2, DoorClosed,
  CheckCircle2, ChevronRight, CalendarDays, MapPin, Navigation,
} from "lucide-react";
import { isToday, isThisISOWeek, parseISO } from "date-fns";
import { maintenanceApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate } from "@/lib/format";
import { buildJobAddress, directionsUrl } from "@/lib/directions";
import { useAuth, isInspectorLike } from "@/context/AuthContext";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";

// Parse a scheduled_for/scheduled_end value (ISO date or timestamp) safely.
const parseSched = (s) => {
  if (!s) return null;
  try {
    const d = s.length === 10 ? parseISO(`${s}T00:00:00`) : parseISO(s);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
};

// Does today fall inside the (inclusive) scheduled window? Multi-day jobs
// (scheduled_end set) count as "today" every calendar day in the range.
const rangeContainsToday = (start, end) => {
  if (!start) return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = end ? new Date(end) : new Date(start); e.setHours(0, 0, 0, 0);
  return t.getTime() >= s.getTime() && t.getTime() <= e.getTime();
};

export default function EngineerJobs() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const dual = isInspectorLike(user);
  const { data, loading, error, reload } = useAsync(() => maintenanceApi.list({ assigned_to_me: true }), []);
  const jobs = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const { urgent, today, week, other, completed } = useMemo(() => {
    const open = jobs.filter((j) => j.status !== "completed");
    const done = jobs
      .filter((j) => j.status === "completed")
      .sort((a, b) => (b.completed_at ? new Date(b.completed_at).getTime() : 0) - (a.completed_at ? new Date(a.completed_at).getTime() : 0))
      .slice(0, 10);
    const u = [], t = [], w = [], o = [];
    for (const j of open) {
      if ((j.priority || "normal").toLowerCase() === "urgent") { u.push(j); continue; }
      const d = parseSched(j.scheduled_for);
      const dEnd = parseSched(j.scheduled_end);
      if (d && (rangeContainsToday(d, dEnd) || isToday(d))) t.push(j);
      else if (d && isThisISOWeek(d)) w.push(j);
      else o.push(j);
    }
    return { urgent: u, today: t, week: w, other: o, completed: done };
  }, [jobs]);

  const openCount = urgent.length + today.length + week.length + other.length;
  const first = user?.name?.split(" ")[0] || "Engineer";
  const onLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="engineer-home">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-lg font-bold text-slate-900">Hi {first}</p>
            <p className="text-xs text-slate-500">{openCount} open job{openCount === 1 ? "" : "s"}</p>
          </div>
          <div className="flex items-center gap-2">
            {dual && (
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-[#ED1C24] hover:bg-red-100"
                data-testid="engineer-switch-inspector"
              >
                <ArrowLeftRight className="h-3.5 w-3.5" /> Inspector view
              </button>
            )}
            <button onClick={onLogout} className="p-2 text-slate-400 hover:text-slate-700" data-testid="engineer-logout" aria-label="Sign out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        {loading && <LoadingState label="Loading your jobs…" />}
        {!loading && error && <ErrorState message={error} onRetry={reload} />}

        {!loading && !error && openCount === 0 && completed.length === 0 && (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm" data-testid="engineer-empty">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-3 text-lg font-semibold text-slate-800">All caught up</p>
            <p className="mt-1 text-sm text-slate-500">New jobs appear here when an inspector assigns one to you.</p>
          </div>
        )}

        {!loading && !error && (openCount > 0 || completed.length > 0) && (
          <div className="space-y-6">
            <JobSection title="🚨 Urgent" tone="text-red-600" jobs={urgent} onOpen={navigate} />
            <JobSection title="Today" tone="text-cyan-600" jobs={today} onOpen={navigate} />
            <JobSection title="This week" tone="text-violet-600" jobs={week} onOpen={navigate} />
            <JobSection title="Open jobs" tone="text-slate-600" jobs={other} onOpen={navigate} />
            {completed.length > 0 && (
              <section>
                <h2 className="mb-2 text-sm font-bold text-slate-500">Recently completed ({completed.length})</h2>
                <div className="space-y-2">
                  {completed.map((j) => <JobCard key={j.id} j={j} done onOpen={navigate} />)}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function JobSection({ title, tone, jobs, onOpen }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className={`text-sm font-bold ${tone}`}>{title}</h2>
        <span className={`text-xs font-semibold ${tone}`}>{jobs.length} job{jobs.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2">
        {jobs.map((j) => <JobCard key={j.id} j={j} onOpen={onOpen} />)}
      </div>
    </section>
  );
}

function JobCard({ j, onOpen, done }) {
  const urgent = (j.priority || "").toLowerCase() === "urgent";
  const addr = buildJobAddress(j);
  const dir = directionsUrl(addr);
  return (
    <Card
      className={`flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md ${done ? "opacity-70" : ""}`}
      onClick={() => onOpen(`/maintenance/${j.id}`)}
      data-testid={`engineer-job-${j.id}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${done ? "bg-emerald-50 text-emerald-600" : "bg-primary/10 text-primary"}`}>
          {done ? <CheckCircle2 className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-800">
            {urgent && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-red-500" />}
            {j.title || j.door_reference || "Maintenance job"}
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 truncate text-sm text-slate-500">
            {j.building_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{j.building_name}</span>}
            {j.door_reference && <span className="inline-flex items-center gap-1"><DoorClosed className="h-3.5 w-3.5" />{j.door_reference}</span>}
          </p>
          {addr && (
            <p className="mt-0.5 flex items-start gap-1 text-xs text-slate-400" data-testid={`engineer-job-address-${j.id}`}>
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-2">{addr}</span>
            </p>
          )}
          {dir && (
            <a
              href={dir}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              data-testid={`engineer-job-directions-${j.id}`}
            >
              <Navigation className="h-3.5 w-3.5" /> Directions
            </a>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {done
          ? <span className="hidden text-xs text-slate-400 sm:inline">{j.completed_at ? fmtDate(j.completed_at) : ""}</span>
          : j.scheduled_for && <span className="hidden items-center gap-1 text-xs text-slate-400 sm:inline-flex"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(j.scheduled_for)}</span>}
        <ChevronRight className="h-5 w-5 text-slate-300" />
      </div>
    </Card>
  );
}
